// utils/environment.ts

// Define the environment constants with proper typing
export const ENVIRONMENT_INSTANCES = {
  DEV: 'dev',
  UAT: 'uat',
  PROD: 'prod',
  LOCAL: 'local',
} as const;

// Extract the type from the values
export type Environment = (typeof ENVIRONMENT_INSTANCES)[keyof typeof ENVIRONMENT_INSTANCES];

export interface EnvironmentConfig {
  name: Environment;
  displayName: string;
  backgroundImage: string;
  headerColor: string;
  backgroundImageLogin: string;
}

export const ENVIRONMENT_CONFIGS: Record<Environment, EnvironmentConfig> = {
  dev: {
    name: 'dev',
    displayName: 'Development Environment',
    backgroundImage: "url('/images/main_bg_dev.png')",
    headerColor: 'bg-green-900',
    backgroundImageLogin: "url('/images/login_bg_dev.png')",
  },
  uat: {
    name: 'uat',
    displayName: 'User Acceptance Testing',
    backgroundImage: "url('/images/main_bg_uat.png')",
    headerColor: 'bg-orange-900',
    backgroundImageLogin: "url('/images/login_bg_uat.png')",
  },
  prod: {
    name: 'prod',
    displayName: 'Production Environment',
    backgroundImage: "url('/images/main_bg.png')",
    headerColor: 'bg-blue-900',
    backgroundImageLogin: "url('/images/login_bg.png')",
  },
  local: {
    name: 'local',
    displayName: 'Local Environment',
    backgroundImage: "url('/images/main_bg_dev.png')",
    headerColor: 'bg-gray-900',
    backgroundImageLogin: "url('/images/login_bg_dev.png')",
  },
};

export const getCurrentEnvironment = (): Environment => {
  const instance = process.env.NEXT_PUBLIC_INSTANCE?.toLowerCase();

  switch (instance) {
    case ENVIRONMENT_INSTANCES.PROD:
      return ENVIRONMENT_INSTANCES.PROD;
    case ENVIRONMENT_INSTANCES.UAT:
      return ENVIRONMENT_INSTANCES.UAT;
    case ENVIRONMENT_INSTANCES.DEV:
      return ENVIRONMENT_INSTANCES.DEV;
    default:
      return ENVIRONMENT_INSTANCES.LOCAL;
  }
};

export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  return ENVIRONMENT_CONFIGS[env];
};

export const isNonProdEnvironment = (): boolean => {
  const env = getCurrentEnvironment();
  return env !== ENVIRONMENT_INSTANCES.PROD;
};

export const shouldEnableSentry = (): boolean => {
  const env = getCurrentEnvironment();
  // Enable Sentry for DEV, UAT, and PROD, but not for LOCAL
  return [ENVIRONMENT_INSTANCES.DEV, ENVIRONMENT_INSTANCES.UAT, ENVIRONMENT_INSTANCES.PROD].includes(
    env as 'dev' | 'uat' | 'prod',
  );
};
